import { FormEvent, useEffect, useState } from "react";
import useAdDashboard from "./hooks/useList";
import "./css/list.scss";

const AdddiLightImageList = () => {
  const hookMember = useAdDashboard();
  const [selectedSnapshot, setSelectedSnapshot] = useState<{
    fileName: string;
    receivedAt: string;
    deviceName: string;
  } | null>(null);
  const [resolution, setResolution] = useState<{ width: number; height: number } | null>(null);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    hookMember.search(true);
  };

  useEffect(() => {
    if (!selectedSnapshot) return;
    const previousOverflow = document.body.style.overflow;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedSnapshot(null);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [selectedSnapshot]);

  const openSnapshot = (fileName: string, receivedAt: string, deviceName: string) => {
    setResolution(null);
    setSelectedSnapshot({ fileName, receivedAt, deviceName });
  };

  const closeSnapshot = () => {
    setSelectedSnapshot(null);
    setResolution(null);
  };

  return (
    <section id="adddi-light-image-list" aria-label="스냅샷 확인">
      <form className="snapshot-toolbar" onSubmit={submitSearch}>
        <label className="snapshot-search" htmlFor="snapshot-device-search">
          <span>기기 검색</span>
          <div>
            <input
              id="snapshot-device-search"
              type="search"
              value={hookMember.filter}
              onChange={(event) => hookMember.setFilter(event.target.value)}
              placeholder="기기 ID 또는 파일명"
              autoComplete="off"
              spellCheck={false}
            />
            {hookMember.filter && (
              <button
                type="button"
                className="is-reset"
                aria-label="검색 초기화"
                onClick={() => hookMember.search(false)}
              >
                ×
              </button>
            )}
            <button type="submit">검색</button>
          </div>
        </label>

        <button
          type="button"
          className={`snapshot-reverse ${hookMember.reverse ? "is-active" : ""}`}
          aria-pressed={hookMember.reverse}
          onClick={() => hookMember.setReverse(!hookMember.reverse)}
        >
          <span aria-hidden="true">↕</span>
          이미지 상하반전
        </button>

        <div className="snapshot-page-control">
          <span>
            총 <strong>{hookMember.totalCount.toLocaleString()}</strong>장
          </span>
          <nav aria-label="스냅샷 페이지">
            <button
              type="button"
              disabled={hookMember.page <= 1}
              aria-label="이전 페이지"
              onClick={() => hookMember.setPage(hookMember.page - 1)}
            >
              ‹
            </button>
            <b>{hookMember.page}</b>
            <small>/ {hookMember.pageLimit}</small>
            <button
              type="button"
              disabled={hookMember.page >= hookMember.pageLimit}
              aria-label="다음 페이지"
              onClick={() => hookMember.setPage(hookMember.page + 1)}
            >
              ›
            </button>
          </nav>
        </div>
      </form>

      {hookMember.loading ? (
        <div className="snapshot-state">
          <span className="snapshot-loader" />
          스냅샷을 불러오는 중입니다.
        </div>
      ) : hookMember.list.length === 0 ? (
        <div className="snapshot-state">조회 조건에 해당하는 스냅샷이 없습니다.</div>
      ) : (
        <div className="snapshot-grid">
          {hookMember.list.map(([fileName, receivedAt]) => {
            const deviceName = fileName.split("_IMAGE")[0];
            return (
              <button
                type="button"
                className="snapshot-card"
                key={fileName}
                aria-label={`${deviceName} 스냅샷 원본 보기`}
                onClick={() => openSnapshot(fileName, receivedAt, deviceName)}
              >
                <div className="snapshot-card__meta">
                  <div>
                    <span>DEVICE</span>
                    <strong title={deviceName}>{deviceName}</strong>
                  </div>
                  <time>{receivedAt}</time>
                </div>
                <div className="snapshot-card__image">
                  <img
                    src={`${process.env.REACT_APP_URL}/uploads/${encodeURIComponent(fileName)}`}
                    alt={`${deviceName} 스냅샷`}
                    loading="lazy"
                    className={hookMember.reverse ? "reverse" : ""}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedSnapshot && (
        <div className="snapshot-modal" role="presentation" onMouseDown={closeSnapshot}>
          <section
            className="snapshot-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="snapshot-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="snapshot-modal__header">
              <div>
                <span>ORIGINAL SNAPSHOT</span>
                <h2 id="snapshot-modal-title">{selectedSnapshot.deviceName}</h2>
                <p>
                  {selectedSnapshot.receivedAt}
                  <b>
                    {resolution ? `${resolution.width.toLocaleString()} × ${resolution.height.toLocaleString()} px` : "해상도 확인 중"}
                  </b>
                </p>
              </div>
              <div className="snapshot-modal__actions">
                <a
                  href={`${process.env.REACT_APP_URL}/uploads/${encodeURIComponent(selectedSnapshot.fileName)}`}
                  download={selectedSnapshot.fileName}
                >
                  이미지 다운로드 ↓
                </a>
                <button type="button" aria-label="원본 이미지 닫기" onClick={closeSnapshot}>×</button>
              </div>
            </header>
            <div className="snapshot-modal__canvas">
              <div>
                <img
                  src={`${process.env.REACT_APP_URL}/uploads/${encodeURIComponent(selectedSnapshot.fileName)}`}
                  alt={`${selectedSnapshot.deviceName} 원본 스냅샷`}
                  className={hookMember.reverse ? "reverse" : ""}
                  onLoad={(event) => {
                    setResolution({
                      width: event.currentTarget.naturalWidth,
                      height: event.currentTarget.naturalHeight,
                    });
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default AdddiLightImageList;
